import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function useProfitLoss(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['reports', 'profit-loss', startDate, endDate],
    queryFn: () => api.reports.profitLoss(startDate, endDate),
    select: (res) => res.data,
    enabled: !!startDate && !!endDate,
  });
}

export function useBalanceSheet(asOfDate: string) {
  return useQuery({
    queryKey: ['reports', 'balance-sheet', asOfDate],
    queryFn: () => api.reports.balanceSheet(asOfDate),
    select: (res) => res.data,
    enabled: !!asOfDate,
  });
}

export function useSalesSummary(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['reports', 'sales-summary', startDate, endDate],
    queryFn: () => api.reports.salesSummary(startDate, endDate),
    select: (res) => res.data,
  });
}

// New hooks for dashboard cards
export function useDashboardCards(days?: number) {
  return useQuery({
    queryKey: ['dashboard', 'cards', days],
    queryFn: () => api.dashboard.cards(days),
    select: (res) => res.data,
  });
}

export function useDashboardRecentOrders(limit?: number) {
  return useQuery({
    queryKey: ['dashboard', 'recent-orders', limit],
    queryFn: () => api.dashboard.recentOrders(limit),
    select: (res) => res.data,
  });
}

export function useDashboardChartData(days?: number) {
  return useQuery({
    queryKey: ['dashboard', 'chart-data', days],
    queryFn: () => api.dashboard.chartData(days),
    select: (res) => res.data,
  });
}

// New hooks for reports cards
export function useReportCards(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['reports', 'cards', startDate, endDate],
    queryFn: () => api.reports.cards(startDate, endDate),
    select: (res) => res.data,
  });
}

export function useReportSalesTrend(
  startDate?: string, 
  endDate?: string, 
  groupBy: 'day' | 'week' | 'month' = 'day'
) {
  return useQuery({
    queryKey: ['reports', 'sales-trend', startDate, endDate, groupBy],
    queryFn: () => api.reports.salesTrend(startDate, endDate, groupBy),
    select: (res) => res.data,
  });
}

export function useAgingReceivables() {
  return useQuery({
    queryKey: ['reports', 'aging-receivables'],
    queryFn: () => api.reports.agingReceivables(),
    select: (res) => res.data,
  });
}

export function usePopularServices(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['reports', 'popular-services', startDate, endDate],
    queryFn: () => api.reports.popularServices(startDate, endDate),
    select: (res) => res.data,
  });
}

export function useExpenseByCategory(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['reports', 'expense-by-category', startDate, endDate],
    queryFn: () => api.reports.expenseByCategory(startDate, endDate),
    select: (res) => res.data,
    enabled: !!startDate && !!endDate,
  });
}
